import React, { useState, useEffect } from "react";
import {
  Box,
  CssBaseline,
  AppBar,
  Toolbar,
  Typography,
  Paper,
  Fade,
  Slide,
  useTheme,
  useMediaQuery,
  ThemeProvider,
  createTheme,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Avatar,
  IconButton,
  Divider,
  keyframes,
  styled,
  Menu,
  MenuItem,
  Badge,
} from "@mui/material";
import {
  Logout as LogoutIcon,
  AddCircle as AddCircleIcon,
  Menu as MenuIcon,
  AccountCircle as AccountCircleIcon,
  ShoppingCart as ShoppingCartIcon,
  Assignment,
  Event,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  removeFromCart,
  updateQuantity,
  clearCart,
  addToCart,
} from "../redux/slices/cartSlice";
import Categories from "./UserProductCat";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../redux/slices/authSlice";
import { useNavigate } from "react-router-dom";
import { Routes, Route } from "react-router-dom";
import CartPage from "./CartPage";
import useAxios from "../useAxios";
import useBlockBack from "./Modals/useBlockBack";
import MyOrders from "./MyOrders";
// Animated gradient background keyframes
const gradientAnimation = keyframes`
  0% { background-position: 0% 50% }
  50% { background-position: 100% 50% }
  100% { background-position: 0% 50% }
`;

// Enhanced theme with dynamic colors
const theme = createTheme({
  palette: {
    primary: {
      main: "#d32f2f",
      light: "#ff6659",
      dark: "#9a0007",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#ff8a80",
      contrastText: "#000000",
    },
    background: {
      default: "#fff5f5",
      paper: "#ffffff",
    },
    text: {
      primary: "#4e0000",
      secondary: "#7f0000",
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", sans-serif',
    h6: {
      fontWeight: 700,
      letterSpacing: "0.5px",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          textTransform: "none",
          fontWeight: 600,
          transition: "all 0.3s ease",
        },
        contained: {
          boxShadow: "0 4px 14px rgba(211, 47, 47, 0.3)",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(211, 47, 47, 0.4)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: "20px",
        },
      },
    },
  },
});

// const Main = styled("main")(({ theme }) => ({
//   flexGrow: 1,
//   //padding: theme.spacing(3),
//   minHeight: "100vh",
//   background: `linear-gradient(-45deg, #fff0f0, #ffebee, #ffdfdf, #ffebee)`,
//   backgroundSize: "400% 400%",
//   animation: `${gradientAnimation} 12s ease infinite`,
//   position: "relative",
//   "&::before": {
//     content: '""',
//     position: "absolute",
//     top: 0,
//     left: 0,
//     right: 0,
//     bottom: 0,
//     backgroundImage:
//       'url("https://www.transparenttextures.com/patterns/brushed-alum.png")',
//     backgroundRepeat: "repeat",
//     opacity: 0.1,
//     pointerEvents: "none",
//   },
// }));

const AppBarStyled = styled(AppBar)(({ theme }) => ({
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  color: theme.palette.text.primary,
  boxShadow: "0 2px 20px rgba(211, 47, 47, 0.1)",
  borderBottom: "1px solid rgba(211, 47, 47, 0.1)",
  transition: "all 0.3s ease",
}));

// const ContentPaper = styled(Paper)(({ theme }) => ({
//   borderRadius: "24%",
//   //  padding: theme.spacing(4),
//   //padding: theme.spacing(2, 3, 4, 5), // [xs, sm, md, lg]
//   //  padding: {
//   //   xs: theme.spacing(2),  // 16px on extra small (mobile)
//   //   sm: theme.spacing(3),  // 24px on small
//   //   md: theme.spacing(4),  // 32px on medium
//   //   lg: theme.spacing(5),  // 40px on large
//   //   xl: theme.spacing(6)   // 48px on extra large
//   // },
//   boxShadow: "0 8px 32px rgba(211, 47, 47, 0.1)",
//   background: "rgba(255, 255, 255, 0.9)",
//   backdropFilter: "blur(12px)",
//   border: "1px solid rgba(255, 152, 152, 0.2)",
//   minHeight: "100vh",
//   transition: "all 0.5s ease",
//   "&:hover": {
//     boxShadow: "0 12px 40px rgba(211, 47, 47, 0.15)",
//     transform: "translateY(-5px)",
//   },
// }));
const Main = styled("main")(({ theme }) => ({
  flexGrow: 1,
  background: `linear-gradient(-45deg, #fff0f0, #ffebee, #ffdfdf, #ffebee)`,
  backgroundSize: "400% 400%",
  animation: `${gradientAnimation} 12s ease infinite`,
  position: "relative",
  overflow: "hidden", // Prevent scrolling at this level
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage:
      'url("https://www.transparenttextures.com/patterns/brushed-alum.png")',
    backgroundRepeat: "repeat",
    opacity: 0.1,
    pointerEvents: "none",
  },
}));

const ContentPaper = styled(Paper)(({ theme }) => ({
  borderRadius: "24px",
  boxShadow: "0 8px 32px rgba(211, 47, 47, 0.1)",
  background: "rgba(255, 255, 255, 0.9)",
  backdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 152, 152, 0.2)",
  height: "calc(104.5vh - 80px)", // Full height minus header
  overflow: "hidden",
  "& > div": {
    // Target the content container
    height: "100%",
    overflow: "auto",
    scrollbarWidth: "none", // Firefox
    "&::-webkit-scrollbar": {
      // Chrome/Safari
      display: "none",
    },
  },
}));
const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const AnimatedButton = styled(Button)({
  animation: `${pulseAnimation} 3s ease infinite`,
});

const UserBadge = styled(Chip)(({ theme }) => ({
  backgroundColor: "#ffebee",
  color: "#d32f2f",
  fontWeight: 600,
  padding: theme.spacing(0.5),
  marginLeft: theme.spacing(1),
  transition: "all 0.3s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 4px 8px rgba(211, 47, 47, 0.2)",
  },
  "& .MuiChip-avatar": {
    backgroundColor: "#d32f2f",
    color: "white",
  },
}));

const UserDashboard = () => {
  const loggedInemail = useSelector((state) => state.auth.email);
  const dispatch = useDispatch();
  const [showCart, setShowCart] = useState(false);
  const cartItems = useSelector((state) => state.cart.items);
  const [openLogoutAlert, setOpenLogoutAlert] = useState(false);
  const theme = useTheme();
  const [cartCount, setCartCount] = useState(0);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const handleClose = () => setOpenLogoutAlert(false);
  const handleLogout = () => setOpenLogoutAlert(true);
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
 const selectedCategory = location.state?.selectedCategory || "";
  const axiosData = useAxios();
  //  useBlockBack("/user-dashboard"); // block going back
  const handleLogoutConfirm = () => {
    localStorage.removeItem("auth");
    dispatch(logout());
    setOpenLogoutAlert(false);
    // Enable back-button guard
    const guard = () => {
      const stored = localStorage.getItem("auth");
      const parsed = stored ? JSON.parse(stored) : null;
      if (!parsed || !parsed.token) {
        navigate("/auth", { replace: true });
      }
    };

    // Save globally so we can remove when login again
    window.__logoutGuardActive = true;
    window.__logoutGuardListener = guard;

    window.addEventListener("popstate", guard);
  };
  const addToCartGet = () => {
    setCartCount(totalItems);
  };
  // Load cart from database on component mount
  useEffect(() => {
    loadCartFromDB();
  }, []);
  const loadCartFromDB = async () => {
    try {
      const storedAuth = localStorage.getItem("auth");
      if (storedAuth) {
        const parsed = JSON.parse(storedAuth);
        const response = await axiosData.get(`getCartData/${parsed.email}`);
        if (response.data && response.data.cart.items) {
          const dataGet = response.data.cart.items;
          const storeData = dataGet.map((data) => {
            dispatch(addToCart(data));
          });
          localStorage.setItem("cart", JSON.stringify(dataGet));
        }
      }
    } catch (err) {
      console.error("Error loading cart from database", err);
    }
  };
  // Mobile menu state
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [cartAnchor, setCartAnchor] = useState(null);
  const handleCartClose = () => setCartAnchor(null);
  // Mobile menu handlers
  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const storedAuth = localStorage.getItem("auth");
        if (storedAuth) {
          const { email } = JSON.parse(storedAuth);
          const res = await axiosData.get(`getBookingData/${email}`);

          // ensure data structure is consistent
          if (Array.isArray(res.data?.bookings)) {
            setOrders(res.data.bookings);
          } else if (res.data?.bookings) {
            setOrders([res.data.bookings]);
          } else {
            setOrders([]);
          }
        }
      } catch (err) {
        console.error("Error loading orders:", err);
      }
    };
    fetchOrders();
  }, []);
  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };
  const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <AppBarStyled
          position="fixed"
          elevation={0}
          sx={{ height: {xs:50,sm:45,md:45}, marginTop: "0px" }}
        >
          <Toolbar
            sx={{
              minHeight: { xs: "50px !important",sm: "50px !important", md: "40px !important" },
              justifyContent: "space-between",
              flexWrap: "wrap",
              marginTop: "0px !important",
            }}
          >
            {/* Logo Section */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                background: "linear-gradient(135deg, #d32f2f 0%, #9a0007 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                animation: `${gradientAnimation} 8s ease infinite`,
                backgroundSize: "100% 100%",
                flexGrow: isMobile ? 1 : 0,
                justifyContent: isMobile ? "center" : "flex-start",
                marginTop: "0px",
              }}
            >
              <Avatar
                src="/Logo2.png"
                variant="square"
                sx={{
                  height: {   xs: 42,   // Mobile: 32px
        sm: 36,   // Small tablet: 36px  
        md: 40,   // Tablet: 40px
        lg: 45,   // Desktop: 45px
        xl: 50,   // Large desktop: 50px
        },
                  width: "auto",
                  mr: { xs: 0,sm:0, md: 2 },
                  aspectRatio: "auto",
                  display: "block",
                  //filter: "drop-shadow(0 2px 4px rgba(211, 47, 47, 0.3))",
                  // transition: "all 0.3s ease",
                  // "&:hover": {
                  //   transform: "rotate(-5deg) scale(1.05)",
                  // },
                }}
              />
            </Box>
            {/* <Box sx={{ flexGrow: 1 }} /> */}
            {/* <Box
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                background: "linear-gradient(135deg, #d32f2f 0%, #9a0007 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                animation: `${gradientAnimation} 8s ease infinite`,
                backgroundSize: "200% 200%",
                position: { md: "absolute" },
                left: { md: "50%" },
                transform: { md: "translateX(-50%)" },
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  //justifyContent:"center",
                  fontWeight: 600,
                  letterSpacing: "1px",
                  fontSize: { md: "1.8rem", lg: "2.125rem" },
                }}
              ></Typography>
            </Box> */}
            {/* <Box sx={{ flexGrow: 1 }} /> */}

            <Slide direction="left" in={!isMobile} mountOnEnter unmountOnExit>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  textAlign: "center",
                 gap: {
                    xs: 1,    // Mobile
                    sm: 1.5,  // Tablet
                    md: 2,    // Laptop
                    lg: 2.5,  // Desktop
                  },
                  flexGrow: isMobile ? 0 : 1,
                  justifyContent: "flex-end",
                }}
              >
                <UserBadge
                  avatar={
                    <Avatar sx={{ width: 30, height: 30 }}>
                      {loggedInemail.charAt(0).toUpperCase()}
                    </Avatar>
                  }
                  label={
                   isMobile ? `${loggedInemail.substring(0, 8)}...` :
                    isTablet ? `${loggedInemail.substring(0, 12)}...` 
                      : loggedInemail
                  }
                  variant="outlined"
                   sx={{
                    fontSize: {
                      xs: '0.75rem',
                      sm: '0.8rem',
                      md: '0.875rem',
                    },
                    height: {
                      xs: 32,
                      sm: 34,
                      md: 36,
                    }
                  }}
                />
               
                <IconButton
                  color="inherit"
                  onClick={() => navigate("/user-dashboard/my-orders")}
                  sx={{ color: "#d32f2f" }} // red theme
                >
                  <Badge badgeContent={orders.length} color="error">
                    <Event />
                  </Badge>
                </IconButton>
                <IconButton
                  color="inherit"
                  onClick={(e) => {
                    setCartAnchor(e.currentTarget);
                  }}
                  sx={{ color: "#d32f2f", marginTop: "5px" }}
                >
                  <Badge badgeContent={totalItems} color="error">
                    <ShoppingCartIcon />
                  </Badge>
                </IconButton>
                 <AnimatedButton
                  variant="contained"
                  color="primary"
                     startIcon={!isTablet && <LogoutIcon />} // Hide icon on tablet for space
                  onClick={handleLogout}
                  sx={{
                    borderRadius: "12px",
                    px: 2,
                    py: 0.5,
                    fontSize: { xs: "0.8rem", md: "1rem" },
                    alignItems: "center",
                  }}
                >
                  Logout
                </AnimatedButton>
              </Box>
            </Slide>

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={handleMobileMenuOpen}
                sx={{ color: "#d32f2f", ml: 1 }}
              >
                <MenuIcon fontSize="large" />
              </IconButton>
            )}
          </Toolbar>
          {/* Mobile Title - Only shown on mobile devices */}
          {/* {isMobile && (
            <Box sx={{ 
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              pb: 1,
              background: 'linear-gradient(135deg, #d32f2f 0%, #9a0007 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: `${gradientAnimation} 8s ease infinite`,
              backgroundSize: '200% 200%',
            }}>
              <Typography variant="h5" sx={{ 
                fontWeight: 600,
                letterSpacing: '1px',
                fontSize: '1.5rem'
              }}>
                User Dashboard
              </Typography>
            </Box>
          )} */}
        </AppBarStyled>
        {/* Mobile Menu */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={handleMobileMenuClose}
          PaperProps={{
            sx: {
              borderRadius: "16px",
              minWidth: "250px",
              boxShadow: "0 10px 30px rgba(211, 47, 47, 0.2)",
              border: "1px solid rgba(211, 47, 47, 0.1)",
              mt: 1,
            },
          }}
        >
          <MenuItem dense sx={{ py: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", px: 1 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  mr: 2,
                  backgroundColor: "#d32f2f",
                  color: "white",
                  fontSize: "1.1rem",
                }}
              >
                {loggedInemail.charAt(0).toUpperCase()}
              </Avatar>
              <Typography
                variant="body1"
                sx={{ fontWeight: 500, color: "#4e0000" }}
              >
                {loggedInemail}
              </Typography>
            </Box>
          </MenuItem>
          <Divider sx={{ my: 0.5, borderColor: "rgba(211, 47, 47, 0.1)" }} />
           
          <MenuItem
            onClick={() => {
              handleMobileMenuClose();
              navigate("/user-dashboard/my-orders");
            }}
            dense
            sx={{ py: 1.5 }}
          >
            <Event sx={{ mr: 2, color: "#d32f2f" }} />
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, color: "#4e0000" }}
            >
              My Orders
            </Typography>
          </MenuItem>
           
          <MenuItem
            onClick={() => {
              handleMobileMenuClose();
            navigate("/user-dashboard/cart");
            }}
            dense
            sx={{ py: 1.5 }}
          >
            <Event sx={{ mr: 2, color: "#d32f2f" }} />
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, color: "#4e0000" }}
            >
              View Cart
            </Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleMobileMenuClose();
              handleLogout();
            }}
            dense
            sx={{ py: 1.5 }}
          >
            <LogoutIcon sx={{ mr: 2, color: "#d32f2f" }} />
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, color: "#4e0000" }}
            >
              Logout
            </Typography>
          </MenuItem>
        </Menu>
        <Menu
          anchorEl={cartAnchor}
          open={Boolean(cartAnchor)}
          onClose={handleCartClose}
          PaperProps={{
            sx: {
              borderRadius: "16px",
              minWidth: "300px",
              maxHeight: "400px",
              overflowY: "auto",
              boxShadow: "0 10px 30px rgba(211, 47, 47, 0.2)",
              border: "1px solid rgba(211, 47, 47, 0.1)",
              mt: 1,
              p: 1,
            },
          }}
        >
          {cartItems.length === 0 ? (
            <Typography sx={{ p: 2, textAlign: "center", color: "#7f0000" }}>
              Your cart is empty
            </Typography>
          ) : (
            cartItems.map((item) => (
              <MenuItem
                key={item.product_id}
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <Typography variant="body2">
                  {item.category === "Virtual Games"
                    ? `${item.name}-Virtual`
                    : item.category === "Touch Screen"
                    ? `${item.name}-Touch`
                    : item.name}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  x {item.quantity}
                </Typography>
              </MenuItem>
            ))
          )}
          {cartItems.length > 0 && (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => {
                  handleCartClose();
                  navigate("/user-dashboard/cart");
                  // TODO: Navigate to full cart page if needed
                }}
              >
                View Cart
              </Button>
            </Box>
          )}
        </Menu>

        {/* Main Content */}
        <Main>
          <Toolbar
            sx={{
              minHeight: { xs: "60px !important", md: "55px !important" },
              pt: isMobile ? 4 : 0,
            }}
          />
          <Fade in={true} timeout={800}>
            <ContentPaper
              elevation={0}
              sx={{
                width: {
                  xs: "95%",
                  sm: "90%",
                  md: "98%",
                },
                mx: "auto",
                my: 0, // Remove vertical margin
              }}
            >
              <Routes>
                <Route
                  path="/"
                  element={<Categories selectedCategoryGet={selectedCategory} />}
                />
                <Route path="cart" element={<CartPage />} />
                <Route path="/my-orders" element={<MyOrders />} />
              </Routes>
              {/* {showCart ? <CartPage onBack={()=>setShowCart(false)}/> : <Categories />} */}
            </ContentPaper>
          </Fade>
        </Main>
      </Box>
      {/* Enhanced Confirmation Dialog with animations */}
      <Dialog
        open={openLogoutAlert}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: "24px",
            overflow: "hidden",
            background: "white",
            boxShadow: "0 15px 40px rgba(211, 47, 47, 0.2)",
            minWidth: isMobile?"350px":"400px",
            transform: "scale(0.9)",
            opacity: 0,
            animation: `${keyframes`
              from { transform: scale(0.9); opacity: 0; }
              to { transform: scale(1); opacity: 1; }
            `} 0.3s ease forwards`,
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            background: "linear-gradient(135deg, #d32f2f 0%, #9a0007 100%)",
            color: "white",
            fontWeight: 700,
            py: 3,
            textAlign: "center",
            fontSize: "1.5rem",
          }}
        >
          Confirm Logout
        </DialogTitle>
        <DialogContent sx={{ py: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 2,
              gap: 3,
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#ffebee",
                color: "#d32f2f",
                width: 80,
                height: 80,
                animation: `${pulseAnimation} 2s ease infinite`,
              }}
            >
              <LogoutIcon fontSize="large" />
            </Avatar>
            <DialogContentText
              id="alert-dialog-description"
              sx={{
                textAlign: "center",
                color: "#4e0000",
                fontSize: "1.1rem",
                maxWidth: "350px",
              }}
            >
              Are you sure you want to logout from your account?
            </DialogContentText>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 4,
            py: 3,
            borderTop: "1px solid rgba(211, 47, 47, 0.1)",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              borderColor: "#d32f2f",
              color: "#d32f2f",
              px: 4,
              py: 1,
              "&:hover": {
                backgroundColor: "#ffebee",
                borderColor: "#d32f2f",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            color="primary"
            autoFocus
            sx={{
              px: 4,
              py: 1,
              boxShadow: "0 4px 14px rgba(211, 47, 47, 0.3)",
              "&:hover": {
                boxShadow: "0 6px 20px rgba(211, 47, 47, 0.4)",
                backgroundColor: "#9a0007",
              },
            }}
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default UserDashboard;
